import React from 'react';
import { isNil, is, propOr } from 'ramda';
import { mapIndexed } from 'ramda-adjunct';
import { Timeline } from 'antd';
import moment from 'moment';
import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons';
import { Dot } from '../components';
import QVersionActions from '../_graphql/queries/QVersionActions';
import colors from '../colors';
import { Glasses, BriefCase, User } from './icons';

const formatDate = date => {
  if (isNil(date)) {
    return '';
  }

  if (is(String, date)) {
    date = parseInt(date);
  }

  return moment(date).format('DD/MM/YYYY HH:mm:ss');
};

const renderNewVersionMetadata = ({ newVersion = false, minor, major }) => {
  if (!newVersion) {
    return null;
  }

  return (
    <span className="inline-flex ml-1.5">
      « Nouvelle version ({major}.{minor}) »
    </span>
  );
};

const getAction = action => propOr({ actionLabel: action }, action, {
  CREATE_OFFER: {
    actionLabel: 'Offre créée',
    renderMetadatas: renderNewVersionMetadata,
    actionColor: colors['flexi-primary-color'],
    icon: <BriefCase />
  },
  CREATE_OFFER_FROM_EXISTING: {
    actionLabel: 'Offre créée à partir d\'une autre',
    actionColor: colors['flexi-primary-color'],
    icon: <BriefCase />
  },
  CREATE_WIDGET: {
    actionLabel: 'Widget créé',
    renderMetadatas: renderNewVersionMetadata,
    actionColor: colors['flexi-primary-color'],
    icon: <BriefCase />
  },
  UPDATE_WIDGET: {
    actionLabel: 'Widget mis à jour',
    actionColor: colors['flexi-primary-color'],
    icon: <CheckCircleFilled />
  },
  CREATE_DOCUMENT: {
    actionLabel: 'Document créé',
    renderMetadatas: renderNewVersionMetadata,
    actionColor: colors['flexi-primary-color'],
    icon: <BriefCase />
  },
  UPDATE_DOCUMENT: {
    actionLabel: 'Document mis à jour',
    actionColor: colors['flexi-primary-color'],
    icon: <CheckCircleFilled />
  },
  CREATE_STYLE: {
    actionLabel: 'Style créé',
    renderMetadatas: renderNewVersionMetadata,
    icon: <BriefCase />
  },
  UPDATE_STYLE: {
    actionLabel: 'Style mis à jour',
    icon: <CheckCircleFilled />
  },
  ACCEPT_REVIEW: {
    actionLabel: 'Revue acceptée',
    actionColor: colors['flexi-valid'],
    icon: <CheckCircleFilled />
  },
  REJECT_REVIEW: {
    actionLabel: 'Revue refusée',
    actionColor: colors['flexi-error'],
    icon: <CloseOutlined />,
    renderMetadatas: ({ rejectionReason }) => (
      <span className="inline-flex ml-1.5 text-flexi-red">
        « {rejectionReason} »
      </span >
    )
  },
  UPDATE_REVIEW_COMMENTS: {
    actionLabel: 'Commentaires de revue mis à jour',
    actionColor: colors['flexi-grey'],
    icon: <CheckCircleFilled />,
    renderMetadatas: ({ comments }) => <p className="italic text-gray-700">{comments}</p>
  },
  UPDATE_TO_DRAFT_MODE: {
    actionLabel: 'Passage en mode édition',
    actionColor: colors['flexi-grey'],
    icon: <Glasses />
  },
  UPDATE_TO_REVIEW_MODE: {
    actionLabel: 'Passage en mode revue',
    actionColor: colors['flexi-grey'],
    icon: <Glasses />
  },
  UPDATE_TO_ARCHIVED_MODE: {
    actionLabel: 'Offre archivée',
    actionColor: colors['flexi-grey'],
    icon: <CheckCircleFilled />
  }
});

const VersionHistory = ({ versionId, itemId }) => {
  return (
    <QVersionActions args={{ versionId, itemId }}>
      {versionActions => {
        return (
          <Timeline mode="left">
            {mapIndexed(({ date, action, userName, metadata }, index) => {
              return (
                <Timeline.Item
                  key={date}
                  label={formatDate(date)}
                  dot={<Dot color={getAction(action).actionColor} width={index === 0 ? 14 : 10} />}
                >
                  <div className="inline-flex items-center text-[14px] font-semibold">
                    <div
                      // eslint-disable-next-line no-restricted-syntax
                      style={{ color: getAction(action).actionColor === colors['flexi-grey'] ? '#000' : getAction(action).actionColor }}
                    >
                      <span className="flex flex-row items-center">
                        <span className="text-[16px] mr-2.5 flex items-center">
                          {getAction(action).icon}
                        </span>
                        <span>
                          {getAction(action).actionLabel}
                          {
                            !isNil(getAction(action).renderMetadatas) &&
                      is(Function, getAction(action).renderMetadatas) &&
                      getAction(action).renderMetadatas(metadata || {})
                          }
                        </span>
                      </span>
                    </div>
                    <span className="text-flexi-grey font-light ml-5 inline-flex items-center gap-1.5">
                      <User />
                      {userName}
                    </span>
                  </div>
                </Timeline.Item>
              );
            })(versionActions)}
          </Timeline>
        );
      }}
    </QVersionActions>
  );
};

export default VersionHistory;
